import React from 'react';
import { SchoolListing } from '../types/api';
import { CheckIcon, StarIcon } from 'lucide-react';
import { parseBulletPoints } from '../utils/htmlParser';
interface SchoolCardProps {
  school: SchoolListing;
  rank: number;
  isRecommended?: boolean;
}
export function SchoolCard({
  school,
  rank,
  isRecommended = false
}: SchoolCardProps) {
  const bullets = parseBulletPoints(school.bodyContent);
  return <a href={school.clickURL} target="_blank" rel="noopener noreferrer" className={`block bg-white rounded-xl overflow-hidden hover:shadow-md transition-all relative group cursor-pointer ${isRecommended ? 'shadow-lg border-2 border-blue-400 bg-gradient-to-br from-blue-50/30 to-indigo-50/20' : 'shadow-sm border border-gray-200'}`} data-id="element-248">
      {/* Recommended Banner */}
      {isRecommended && <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-2 px-4 font-bold text-sm flex items-center justify-center gap-2" data-id="element-249">
          <span className="text-lg" data-id="element-250">🏆</span>
          <span data-id="element-251">#1 Recommended School</span>
        </div>}

      {/* Top Highlight Badge */}
      {school.schoolHighlight && <div className={`absolute ${isRecommended ? 'top-10' : 'top-0'} left-0 bg-[#1a2332] text-white text-xs font-bold px-4 py-1.5 rounded-br-lg z-10 flex items-center gap-1.5`} data-id="element-252">
          <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-253" />
          {school.schoolHighlight}
        </div>}

      <div className={`p-4 md:p-6 ${isRecommended && school.schoolHighlight ? 'pt-12 md:pt-14' : school.schoolHighlight ? 'pt-12 md:pt-14' : isRecommended ? 'pt-2' : ''}`} data-id="element-254">
        {/* ===== MOBILE LAYOUT ===== */}
        <div className="md:hidden" data-id="element-255">
          {/* Row 1: Rank + Logo + CTA */}
          <div className="flex items-center gap-3 mb-3" data-id="element-256">
            <div className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 font-medium text-xs flex-shrink-0" data-id="element-257">
              {rank}
            </div>
            <div className="w-32 h-20 flex items-center justify-center bg-white rounded border border-gray-100 p-2 flex-shrink-0" data-id="element-258">
              <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-259" />
            </div>
            <div className="ml-auto bg-[#e8594f] group-hover:bg-[#d64a41] text-white font-bold py-2.5 px-4 rounded-lg text-center transition-colors shadow-sm text-sm whitespace-nowrap" data-id="element-260">
              View Programs
            </div>
          </div>

          {/* Row 2: School name + headContent */}
          <h2 className="text-base font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors mb-0.5 leading-snug" data-id="element-261">
            {school.mBrandName}
          </h2>

          {school.headContent && <p className="text-sm text-gray-600 mb-3 leading-snug" data-id="element-262">
              {school.headContent}
            </p>}

          {/* Bullets */}
          <ul className="space-y-2 mb-2" data-id="element-263">
            {bullets.slice(0, 3).map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed" data-id="element-264">
                <CheckIcon className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-265" />
                <span data-id="element-266">{bullet}</span>
              </li>)}
          </ul>

          <div className="flex items-center gap-1.5 text-xs font-bold text-[#22c55e]" data-id="element-267">
            <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-268" />
            100% Online
          </div>
        </div>

        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="hidden md:flex gap-6" data-id="element-269">
          {/* Left: Rank */}
          <div className="flex items-start pt-1 flex-shrink-0" data-id="element-270">
            <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 font-medium text-sm flex-shrink-0" data-id="element-271">
              {rank}
            </div>
          </div>

          {/* Center: Content */}
          <div className="flex-grow min-w-0" data-id="element-272">
            <h2 className="text-lg font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors mb-0.5 leading-snug" data-id="element-273">
              {school.mBrandName}
            </h2>

            {school.headContent && <p className="text-sm text-gray-600 mb-3 leading-snug" data-id="element-274">
                {school.headContent}
              </p>}

            <ul className="space-y-2 mb-1" data-id="element-275">
              {bullets.slice(0, 4).map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed" data-id="element-276">
                  <CheckIcon className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-277" />
                  <span data-id="element-278">{bullet}</span>
                </li>)}
            </ul>
          </div>

          {/* Right: Logo + CTA */}
          <div className="flex flex-col items-center w-44 flex-shrink-0 border-l border-gray-100 pl-5" data-id="element-279">
            <div className="w-36 h-20 flex items-center justify-center bg-white rounded-lg border border-gray-100 p-2 group-hover:border-gray-300 transition-colors mb-3" data-id="element-280">
              <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-281" />
            </div>

            <div className="w-full bg-[#e8594f] group-hover:bg-[#d64a41] text-white font-bold py-3 px-4 rounded-lg text-center transition-colors shadow-sm text-sm" data-id="element-282">
              View Programs
            </div>

            <div className="mt-3 flex items-center gap-1.5 text-sm font-bold text-[#22c55e]" data-id="element-283">
              <StarIcon className="w-4 h-4 fill-current" data-id="element-284" />
              100% Online
            </div>
          </div>
        </div>
      </div>
    </a>;
}